import axios from 'axios';
import type { VerificationScope } from './verificationApi';

export const registrationStages = ['not_started', 'submitting', 'business_review', 'campaign_review', 'number_registration', 'needs_attention', 'complete'] as const;
export interface VerificationStatus {
  enabled: true;
  businessId: string;
  environment: 'production' | 'staging';
  simulated: boolean;
  activationStatus: 'pending_activation' | 'cancelled' | 'active';
  canManage: boolean;
  registration: {
    stage: typeof registrationStages[number];
    title: string;
    message: string;
    complete: boolean;
    checkedAt?: string;
  };
}
export type VerificationStatusResponse = VerificationStatus | { enabled: false };
const object = (value: unknown): value is Record<string, any> => !!value && typeof value === 'object' && !Array.isArray(value);

/** This member-safe response never contains the owner's application or payment details. */
export function validateVerificationStatus(value: unknown, scope: VerificationScope): VerificationStatusResponse {
  if (object(value) && value.enabled === false) return { enabled: false };
  if (!object(value) || value.enabled !== true || value.businessId !== scope.businessId ||
    !['production', 'staging'].includes(value.environment) || typeof value.simulated !== 'boolean' ||
    (value.simulated && value.environment !== 'staging') || typeof value.canManage !== 'boolean' ||
    !['pending_activation', 'cancelled', 'active'].includes(value.activationStatus) ||
    !object(value.registration) || !registrationStages.includes(value.registration.stage) ||
    typeof value.registration.complete !== 'boolean' || value.registration.complete !== (value.registration.stage === 'complete') ||
    !['title', 'message'].every(key => typeof value.registration[key] === 'string' && value.registration[key].trim().length > 0 && value.registration[key].length <= 600) ||
    (value.registration.checkedAt !== undefined && (typeof value.registration.checkedAt !== 'string' || !Number.isFinite(Date.parse(value.registration.checkedAt))))) {
    throw new Error('We couldn’t confirm your texting status.');
  }
  return value as VerificationStatus;
}

export async function loadVerificationStatus(scope: VerificationScope, signal?: AbortSignal): Promise<VerificationStatusResponse> {
  const { data } = await axios.get('/api/onboarding/verification/status', {
    signal, timeout: 15000, _suppressErrorToast: true,
    headers: { 'X-Onboarding-Business': scope.businessId, 'X-Onboarding-User': scope.userId },
  } as Parameters<typeof axios.get>[1]);
  return validateVerificationStatus(data, scope);
}
